/**
 * Popup Registry Service
 *
 * Tracks open popup windows to prevent duplicates and handle cleanup.
 * Each item can only have one popup window open at a time.
 */

// ============================================
// TYPES
// ============================================

interface PopupEntry {
  itemId: string;
  window: Window;
  cleanup: () => void;
}

// ============================================
// STATE
// ============================================

const openPopups = new Map<string, PopupEntry>();

// ============================================
// PUBLIC API
// ============================================

/**
 * Get the popup window for a specific item
 * @returns The popup window if open and not closed, null otherwise
 */
export function getPopupForItem(itemId: string): Window | null {
  const entry = openPopups.get(itemId);
  if (entry && !entry.window.closed) {
    return entry.window;
  }
  // Clean up stale entry
  if (entry) {
    openPopups.delete(itemId);
  }
  return null;
}

/**
 * Register a new popup window
 * If a popup already exists for this item, it will be closed first.
 */
export function registerPopup(itemId: string, win: Window, cleanup: () => void): void {
  // Clean up existing if any
  const existing = openPopups.get(itemId);
  if (existing && !existing.window.closed) {
    existing.cleanup();
    existing.window.close();
  }

  openPopups.set(itemId, { itemId, window: win, cleanup });
}

/**
 * Unregister a popup window (called when popup closes)
 */
export function unregisterPopup(itemId: string): void {
  openPopups.delete(itemId);
}

/**
 * Focus an existing popup window if open
 * @returns true if popup exists and was focused, false otherwise
 */
export function focusExistingPopup(itemId: string): boolean {
  const win = getPopupForItem(itemId);
  if (win) {
    win.focus();
    return true;
  }
  return false;
}

/**
 * Check if a popup is currently open for an item
 */
export function isPopupOpen(itemId: string): boolean {
  return getPopupForItem(itemId) !== null;
}

/**
 * Close all open popups (for cleanup on app unmount or logout)
 */
export function closeAllPopups(): void {
  openPopups.forEach((entry) => {
    if (!entry.window.closed) {
      entry.cleanup();
      entry.window.close();
    }
  });
  openPopups.clear();
}

/**
 * Get count of currently open popups
 */
export function getOpenPopupCount(): number {
  // Clean up closed windows first
  openPopups.forEach((entry, itemId) => {
    if (entry.window.closed) {
      openPopups.delete(itemId);
    }
  });
  return openPopups.size;
}
